import { useEffect, useState } from 'react';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import type { MissingFormLike, ReviewFinding, ReviewFormDoc, ReviewSummaryLike } from './protocolReview';

export interface ReviewSummary extends ReviewSummaryLike {
  runId: string;
  completedAt?: unknown;
  missingFormsError?: string | null;
  startedAt?: unknown;
  /** ISO string; set at every claim including a continuation. */
  claimedAt?: unknown;
  missingForms?: Array<MissingFormLike & { protocolQuote?: string | null; quoteVerified?: boolean }>;
}

export interface ReviewFormRecord extends ReviewFormDoc {
  formId: string;
  formName?: string;
  findings: ReviewFinding[];
}

/** Live listeners on the review summary and per-form results. Read only. */
export function useProtocolReview(projectId: string | undefined) {
  const [summary, setSummary] = useState<ReviewSummary | null>(null);
  const [forms, setForms] = useState<Record<string, ReviewFormRecord>>({});

  useEffect(() => {
    if (!projectId) return undefined;
    return onSnapshot(
      doc(db, 'projects', projectId, 'crfReview', 'summary'),
      (snap) => setSummary(snap.exists() ? (snap.data() as ReviewSummary) : null),
      () => setSummary(null),
    );
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return undefined;
    return onSnapshot(
      collection(db, 'projects', projectId, 'crfReviewForms'),
      (snap) => {
        const next: Record<string, ReviewFormRecord> = {};
        snap.forEach((d) => {
          const data = d.data() as ReviewFormRecord;
          next[d.id] = { ...data, findings: Array.isArray(data.findings) ? data.findings : [] };
        });
        setForms(next);
      },
      () => setForms({}),
    );
  }, [projectId]);

  return { summary, reviewForms: forms };
}
